/*
 * Padronização de datas/horários do cronograma de torneios.
 *
 * O backend guarda o horário "de parede" do torneio em UTC:
 *   "2026-06-26T12:00:00.000Z"  representa  12:00
 * ou seja, NÃO é um instante a ser convertido pelo fuso do navegador. Por isso
 * toda leitura/exibição usa métodos UTC, e o envio usa uma string wall-clock
 * sem 'Z' — o mesmo formato em que `date`/`end_date` são enviados (datetime-local).
 */

/** "2026-06-26T12:00:00.000Z" -> "12:00" (interpreta o ISO como wall-clock UTC). */
export const formatWallClockTime = (iso?: string | null): string => {
  if (!iso) return '--:--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(
    d.getUTCMinutes(),
  ).padStart(2, '0')}`;
};

/**
 * Monta a string wall-clock (sem 'Z') para enviar ao backend, no mesmo formato
 * de `date`/`end_date`. Enviar com 'Z' (instante UTC) faz o servidor reconverter
 * pelo fuso e devolver o horário deslocado.
 */
export const buildWallClockIso = (
  baseIso: string,
  timeHHmm: string,
): string => {
  const datePart = (baseIso || new Date().toISOString()).slice(0, 10);
  return `${datePart}T${timeHHmm}:00`;
};

/** Data/hora completa do torneio em pt-BR, interpretando o ISO como wall-clock (UTC). */
export const formatTournamentDateRange = (
  startStr?: string | null,
  endStr?: string | null,
): string => {
  if (!startStr) return '';
  try {
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;

    const sameDay =
      end != null &&
      start.getUTCFullYear() === end.getUTCFullYear() &&
      start.getUTCMonth() === end.getUTCMonth() &&
      start.getUTCDate() === end.getUTCDate();

    const dateNoWeekday = (d: Date) =>
      d.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      });

    const hourLabel = (d: Date) => {
      const h = d.getUTCHours();
      const m = d.getUTCMinutes();
      return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
    };

    if (end && !sameDay) {
      return `De ${dateNoWeekday(start)} às ${hourLabel(start)} até ${dateNoWeekday(end)} às ${hourLabel(end)}.`;
    }

    const fullDate = start.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
      timeZone: 'UTC',
    });
    const startTime = start.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
    const endTime = end
      ? end.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        })
      : null;

    return endTime
      ? `${fullDate}, ${startTime} às ${endTime}.`
      : `${fullDate}, ${startTime}`;
  } catch {
    return startStr;
  }
};
